import { lazy } from "react";

/**
 * Chunk-load failure recovery. After a deployment, old chunk filenames become
 * invalid: the server returns index.html (HTML) instead of JS, causing
 * "SyntaxError: Unexpected token '<'". Reloading the page picks up the new
 * index.html and its fresh chunk URLs (per Vite's Load Error Handling docs).
 *
 * Reloads are capped per path per session with a counter — NOT re-armed on
 * successful loads. Re-arming on success loops forever with sequential lazy
 * chains (Project loads OK → clears guard → Board chunk persistently fails →
 * reload → repeat). The counter gives later deploys in a long session their
 * own recovery reload while keeping a hard bound: a persistent failure burns
 * through the cap and then surfaces to AppErrorBoundary's Reload button.
 */
const MAX_RELOADS_PER_PATH = 3;

// Indirection because jsdom's location.reload is unforgeable — tests stub this.
export const pageReload = { trigger: () => window.location.reload() };

/**
 * Reload the page unless this path has exhausted its reload budget.
 * Returns true if a reload was triggered.
 */
export function reloadOnChunkError(): boolean {
  const key = "chunk_reload_" + window.location.pathname;
  const count = Number(sessionStorage.getItem(key)) || 0;
  if (count >= MAX_RELOADS_PER_PATH) return false;
  sessionStorage.setItem(key, String(count + 1));
  pageReload.trigger();
  return true;
}

export function importWithRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch((error: Error) => {
    if (reloadOnChunkError()) {
      // Return a never-resolving promise since the page is reloading
      return new Promise<T>(() => {});
    }
    // Reload budget exhausted — surface the real error
    throw error;
  });
}

export function lazyWithRetry(
  importFn: () => Promise<{ default: React.ComponentType<any> }>,
) {
  return lazy(() => importWithRetry(importFn));
}
